import WorksheetForm from '~/features/projectWizard/worksheets/common/components/WorksheetForm';
import { WorksheetField } from '~/features/projectWizard/worksheets/common/components/Field';
import { ContractType, ContractTypeDropdownOptions, DeploymentTypes, PricingType, RevenueSource, UnitDecimals } from '~/constants';
import { InputType } from '~/schema';
import { useProjectContext } from '~/hooks';
import { formatNumber } from '~/utils';
import RateText from './RateText';
import { useDerivedContractFields } from '~/features/projectWizard/worksheets/common/derived/useDerivedContractFields';

export default function Contract () {
  const { project } = useProjectContext();
  useDerivedContractFields();

  let communitySolarRevenueRate: number | undefined = undefined;
  if (
    project?.revenueSource === RevenueSource.COMMUNITY_SOLAR_SUBS &&
    project?.contractRate != null &&
    project?.communitySolarDiscountPercentage != null
  ) {
    communitySolarRevenueRate = project.contractRate * (1 - project.communitySolarDiscountPercentage / 100);
  }

  return (
    <WorksheetForm>
      <WorksheetField
        type="radio"
        fieldLabel="Do you have recommended revenue assumptions?"
        schemaKey="revenueAssumptionsFlag"
        values={[
          { label: 'Yes, I want to recommend revenue assumptions', value: true },
          { label: 'No, I want the investor to make their own revenue assumptions', value: false },
        ]}
      />
      <WorksheetField
        type="radio"
        schemaKey="contractType"
        fieldLabel="Contract Type"
        values={ContractTypeDropdownOptions.filter(option => (
          project.deploymentType === DeploymentTypes.UTILITY && 
          (project.pricingType === PricingType.ACQUISITION || project.pricingType === PricingType.DEV_FEE)) ? true : option.value !== ContractType.UNCONTRACTED)}
        inline
        stretched
      />
      { project.contractType === ContractType.UNCONTRACTED ? 
        <WorksheetField
          type="number"
          fieldLabel="Contract term"
          schemaKey="contractTerm"
          placeholder="TBD"
          label="years"
          decimals={0}
          disabled
          helpText='This will match your site lease term.'
        />
        :
        <WorksheetField
          type="number"
          fieldLabel="Contract term"
          schemaKey="contractTerm"
          placeholder="Enter contract term"
          label="years"
          decimals={0}
        />}
      <WorksheetField
        type="number"
        fieldLabel="Offtake rate"
        placeholder="Enter offtake rate"
        schemaKey="contractRate"
        label={project.contractType === ContractType.PPA || project.contractType === ContractType.UNCONTRACTED ? 'per kWh' : 'per year'}
        numberType={InputType.DOLLARS}
        decimals={project.contractType === ContractType.PPA || project.contractType === ContractType.UNCONTRACTED ? UnitDecimals.KWH : UnitDecimals.YEAR}
      />
      {project.pricingType === PricingType.OFFTAKE ? 
        <RateText /> : null
      }
      <WorksheetField
        type="number"
        fieldLabel="Discount percentage"
        placeholder="Enter discount percentage"
        schemaKey="communitySolarDiscountPercentage"
        numberType={InputType.PERCENT}
        decimals={UnitDecimals.PERCENT}
        helpText="Blended discount to the base power rate provided"
      />
      <WorksheetField
        type="number"
        fieldLabel="Annual contract escalator"
        placeholder="Enter contract escalator"
        helpText="1-2% is most common."
        schemaKey="contractEscalator"
        numberType={InputType.PERCENT}
        decimals={UnitDecimals.PERCENT}
      />
      {project.revenueSource === RevenueSource.COMMUNITY_SOLAR_SUBS && project.revenueAssumptionsFlag ? (
        <div
          style={{
            background: 'rgba(3, 89, 140, 0.06)',
            border: '1px solid rgba(3, 89, 140, 0.18)',
            borderRadius: '0.25rem',
            padding: 'var(--medium)',
          }}
        >
          <div
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'var(--color-charcoal)',
            }}
          >
            Community solar revenue rate
          </div>
          <div
            style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              lineHeight: 1.2,
              marginTop: 'var(--2x-small)',
              color: communitySolarRevenueRate != null ? 'var(--color-black)' : 'var(--color-charcoal)',
            }}
          >
            {communitySolarRevenueRate != null
              ? `$${formatNumber(communitySolarRevenueRate, UnitDecimals.KWH)} `
              : 'N/A — more information needed'}
            {communitySolarRevenueRate != null && (
              <span style={{ fontSize: '0.875rem', fontWeight: 400, color: 'var(--color-charcoal)' }}>
                per kWh
              </span>
            )}
          </div>
          <p style={{ marginTop: 'var(--2x-small)', color: 'var(--color-charcoal)' }}>
            Expected $/kWh rate which will accrue to the project owner.
          </p>
        </div>
      ) : null}
      
      <WorksheetField
        type="textarea"
        fieldLabel="Community solar revenue details"
        schemaKey="communitySolarRevenueDetails"
        placeholder="Add any revenue assumption details or perspectives here."
        optional
      />
    </WorksheetForm>
  );
}